import React, { Suspense, useEffect, useState, useCallback, lazy } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { routes } from "./routes/routes";
import Router from "./components/Router/Router";
import { LocalizationProvider } from "./locale/localizationContext";
import { ThemeProvider } from "@mui/material";
import { v4 } from "uuid";
import { getFromLocalStorage, setOnLocalStorage } from "./helpers/localstorage";
import { get, has } from "lodash";
import Loading from "./components/Loading/Loading";
import GeneralError from "./helpers/ErrorBoundary";
import { ErrorBoundary } from "react-error-boundary";
import { getCookie, setCookie } from "./helpers/cookie";
import { RoleEnum } from "models/role.types";
import Config from "Config";
import { AgencyType } from "models/product.types";
import { logoutApi, myAgency } from "api/auth.api";
import ErrorAPI from "helpers/api";
import CookieConsent from "react-cookie-consent";
import Footer from "components/Footer/Footer";
import DomainRedirectPage from "pages/domainRedirect";
import MisuChat from "components/MisuChat/MisuChat";

import colorsDefault from './assets/style/colors-default.json';
import colorsNikon from './assets/style/colors-nikon.json';
import './App.scss';
import { FormulaFlowProvider } from "hooks/FormulaFlowContext";

type AppProps = {};

type CookieConsentType = {
  accepted: boolean;
  timestamp?: string;
}

const isLegacyAppHost = () =>
  window.location.hostname.toLowerCase() === Config.legacyAppHostname.toLowerCase();

export const App: React.FC<AppProps> = () => {
  if (isLegacyAppHost()) {
    return (
      <Suspense fallback={<Loading />}>
        <ThemeProvider theme={require("./mui/theme").default}>
          <LocalizationProvider loader={<Loading />}>
            <DomainRedirectPage />
          </LocalizationProvider>
        </ThemeProvider>
      </Suspense>
    );
  }

  return <MainApp />;
};

const MainApp: React.FC<AppProps> = () => {
  const [isAppInitialized, setIsAppInitialized] = useState(false);
  const token = getCookie("token");
  const role = getCookie("role");
  const { agencyName, emetteFattura } = (getFromLocalStorage() as any) || {};
  const [theme, setTheme] = useState<any>(require("./mui/partialTheme").default);
  
  useEffect(() => {
    const loadCss = async () => {
      console.log('setting colors');
      if (String(agencyName).toLowerCase() === 'nikon' || String(agencyName).toLowerCase() === 'nital') {
        Object.keys(colorsNikon).forEach(k => document.documentElement.style.setProperty(k, colorsNikon[k as keyof typeof colorsNikon]));
      }
      else {
        Object.keys(colorsDefault).forEach(k => document.documentElement.style.setProperty(k, colorsDefault[k as keyof typeof colorsDefault]));
      }
      setTheme(require("./mui/theme").default);
    }

    loadCss();
  }, [agencyName]);

  
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (!has(getFromLocalStorage(), "app-run-id")) {
      const uuid = v4();
      setOnLocalStorage({ "app-run-id": uuid });
    }
    setOnLocalStorage({
      "app-launch-count": get(getFromLocalStorage(), "app-launch-count", 0) + 1,
    });
  }, []);


  // cookies consent
  const [consent, setConsent] = useState<CookieConsentType | null>();
  useEffect(() => {
    const savedConsent = localStorage.getItem("cookieConsent");
    if (savedConsent) {
      setConsent(JSON.parse(savedConsent));
    }
  }, []);
  const handleAccept = () => {
    setConsent({ accepted: true, timestamp: new Date().toISOString() });
    localStorage.setItem("cookieConsent", JSON.stringify({ accepted: true, timestamp: new Date().toISOString() }));
    // Chiamata API per salvare il consenso nel database
    // fetch("/api/save-cookie-consent", {
    //   method: "POST",
    //   headers: { "Content-Type": "application/json" },
    //   body: JSON.stringify({ accepted: true, timestamp: new Date().toISOString() })
    // });
  };

  useEffect(() => {
    const validateRole = async () => {
      if (!!token) {
        setIsAppInitialized(false);
        try {
          const { payload: agencyPayload, body: agencyBody } = await myAgency();
          if (agencyPayload.ok) {
            if (agencyBody) {
              const arrayC = agencyBody.agencies;
              console.log('arrayC', arrayC);
              setCookie("agencies", JSON.stringify(arrayC.sort((a: AgencyType, b: AgencyType) => a.agency.name?.localeCompare(b.agency.name ?? "") ?? 0)));
              const smartCom = (arrayC || []).find(
                (a: AgencyType) =>
                  a?.agency.name === "SmartCom" &&
                  (a.role === RoleEnum.SUPERADMINISTRATOR ||
                    a.role === RoleEnum.ADMINISTRATOR)
              );
              if (!!smartCom) {
                if (role !== smartCom.role) {
                  setCookie("role", smartCom.role);
                }
              } else {
                if (role !== arrayC[0].role) {
                  setCookie("role", arrayC[0].role);
                }
              }

              const currentPage = location.pathname;
              if (currentPage !== "/choose-company" && !agencyName) {
                history.push({
                  pathname: "/choose-company",
                  state: {
                    agencies: arrayC,
                  },
                });
              }
            }
          }
        } catch (e) {
          if (e instanceof ErrorAPI) {
            console.log('e', e);
            logoutApi();
          } else {
            console.log('e', e);
            throw e;
          }
        }
      }

      setIsAppInitialized(true);
    };

    validateRole();
  }, [role, token, agencyName]);

  const fallback = useCallback(() => <GeneralError />, []);

  const computePermissions = useCallback(() => {
    const { roleScopes } = Config;
    let p = {};
    switch (role || "") {
      case RoleEnum.SUPERADMINISTRATOR:
        p = roleScopes.SUPERADMINISTRATOR;
        break;
      case RoleEnum.ADMINISTRATOR:
        p = roleScopes.ADMINISTRATOR;
        break;
      case RoleEnum.AGENCY_ADMIN:
        p = roleScopes.AGENCY_ADMIN;
        break;
      case RoleEnum.AGENCY:
        p = roleScopes.AGENCY;
        break;
      case RoleEnum.AGENT:
        p = roleScopes.AGENT;
        break;
      case RoleEnum.JUNIOR_AGENT:
        p = roleScopes.JUNIOR_AGENT;
        break;
      case RoleEnum.CAPO_AREA:
        p = roleScopes.CAPO_AREA;
        break;
      case RoleEnum.GROUP_MANAGER:
        p = roleScopes.GROUP_MANAGER;
        break;
      case RoleEnum.CUSTOMER:
        p = roleScopes.CUSTOMER;
        break;
      default:
        break;
    }

    if (!emetteFattura && role !== RoleEnum.SUPERADMINISTRATOR && role !== RoleEnum.ADMINISTRATOR) {
      delete (p as any).commissions;
    }

    return p;
  }, [role, emetteFattura]);

  return (
    <Suspense fallback={<Loading />}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider loader={<Loading />}>
          <FormulaFlowProvider>
            {!isAppInitialized ? (
              <Loading />
            ) : (
              <ErrorBoundary fallbackRender={fallback}>
                <Router
                  authorized={!!token && !!role}
                  routes={routes}
                  permissions={computePermissions()}
                />
              </ErrorBoundary>
            )}
            <CookieConsent
              onAccept={handleAccept}
              buttonText="Accetto"
              disableStyles={true}
              containerClasses="CookieConsent cookie-consent-bar"
              contentClasses="cookie-consent-bar__content"
              buttonWrapperClasses="cookie-consent-bar__actions"
              buttonClasses="cookie-accept-button"
            >
              Questo sito utilizza cookie per migliorare l'esperienza utente.
            </CookieConsent>
            <MisuChat visible={!!token && !!role} />
          </FormulaFlowProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </Suspense>
  );
};

export default App;
